/* eslint-disable no-unused-vars */
import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/navigationBar";
import { ListUsers, updateUser, deleteUser } from "../../api";
import Swal from "sweetalert2";
import Loader from "../../components/loader";
import "./ListAkunNasabah.scss";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Edit2, Trash2, X, Save, User } from "lucide-react";

export default function ListAkunNasabah() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [updateName, setUpdateName] = useState("");
  const [updatePhone, setUpdatePhone] = useState("");
  const [updateNik, setUpdateNik] = useState("");
  const BASE_URL = "";
  const [isLoading, setIsLoading] = useState(true);
  const [showPhoto, setShowPhoto] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const swalOnTop = { customClass: { container: "swal2-on-top" } };

  const getData = async () => {
    setIsLoading(true);
    try {
      const data = await ListUsers();
      setUsers(data);
    } catch {
      setUsers([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const deferredSearch = useDeferredValue(search);
  const filteredUsers = useMemo(() => {
    const keyword = deferredSearch.trim().toLowerCase();
    if (!keyword) return users;

    return users.filter((user) =>
      [user.name, user.phone, user.info_pinjam?.[0]?.no_ktp]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [users, deferredSearch]);

  const handleUpdateClick = (user) => {
    setSelectedUser(user);
    setUpdateName(user.name || "");
    setUpdatePhone(user.phone || "");
    setUpdateNik(user.info_pinjam?.[0]?.no_ktp || "");
    setShowUpdateForm(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateUser(selectedUser._id, {
        name: updateName,
        phone: updatePhone,
        nik: updateNik,
      });
      setShowUpdateForm(false);
      setSelectedUser(null);
      getData(); // Refresh data

      Swal.fire({
        ...swalOnTop,
        icon: "success",
        title: "Berhasil Update Data User",
        confirmButtonColor: "#4f46e5",
      });
    } catch (err) {
      Swal.fire({
        ...swalOnTop,
        icon: "error",
        title: "Gagal Update Data User",
        text: err.response?.msg || "Terjadi kesalahan. Coba lagi.",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handlePhotoClick = (user) => {
    const url =
      user.info_pinjam &&
        user.info_pinjam[0] &&
        user.info_pinjam[0].foto &&
        user.info_pinjam[0].foto[0] &&
        user.info_pinjam[0].foto[0].foto_ktp
        ? `${BASE_URL}/${user.info_pinjam[0].foto[0].foto_ktp}`
        : null;

    if (url) {
      setPhotoUrl(url);
      setShowPhoto(true);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    const konfirmasi = await Swal.fire({
      ...swalOnTop,
      title: "Hapus User?",
      text: "Data user dan seluruh tagihan akan dihapus permanen!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#aaa",
    });
    if (!konfirmasi.isConfirmed) return;

    try {
      await deleteUser(selectedUser._id);
      setShowUpdateForm(false);
      setSelectedUser(null);
      Swal.fire({
        ...swalOnTop,
        icon: "success",
        title: "User berhasil dihapus",
        confirmButtonColor: "#4f46e5",
      });
      getData();
    } catch (err) {
      Swal.fire({
        ...swalOnTop,
        icon: "error",
        title: "Gagal hapus user",
        text: err.message || "Terjadi kesalahan. Coba lagi.",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  return (
    <div className="list-akun-root">
      <Navbar />
      <div className="list-akun-content">
        <div className="header-actions">
          <h3>List Akun Nasabah</h3>
          <div className="search-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Cari nama, telepon, atau NIK..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Foto</th>
                  <th>Nama</th>
                  <th>No Telepon</th>
                  <th>NIK</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: "30px", color: "var(--text-secondary)" }}>
                      Tidak ada data nasabah ditemukan.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, idx) => (
                    <motion.tr
                      key={user._id || idx}
                      initial={false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <td style={{ textAlign: 'center' }}>
                        <img
                          src={
                            user.info_pinjam &&
                              user.info_pinjam[0] &&
                              user.info_pinjam[0].foto &&
                              user.info_pinjam[0].foto[0] &&
                              user.info_pinjam[0].foto[0].foto_ktp
                              ? `${BASE_URL}/${user.info_pinjam[0].foto[0].foto_ktp}`
                              : "https://icons.veryicon.com/png/o/miscellaneous/bitisland-world/person-18.png"
                          }
                          alt="Foto"
                          className="user-photo"
                          onClick={() => handlePhotoClick(user)}
                        />
                      </td>
                      <td>{user.name || "-"}</td>
                      <td>{user.phone || "-"}</td>
                      <td>{user.info_pinjam?.[0]?.no_ktp || "-"}</td>
                      <td>
                        <button
                          className="action-btn"
                          title="Update User"
                          onClick={() => handleUpdateClick(user)}
                        >
                          <Edit2 size={18} />
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showUpdateForm && (
          <div className="modal-overlay" onClick={() => setShowUpdateForm(false)}>
            <motion.div
              className="modal-update"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleUpdateSubmit}>
                <h3>Update Data User</h3>
                <label>Nama</label>
                <input
                  type="text"
                  value={updateName}
                  onChange={(e) => setUpdateName(e.target.value)}
                  required
                />
                <label>No Telepon</label>
                <input
                  type="text"
                  value={updatePhone}
                  onChange={(e) => setUpdatePhone(e.target.value)}
                  required
                />
                <label>NIK</label>
                <input
                  type="text"
                  value={updateNik}
                  onChange={(e) => setUpdateNik(e.target.value)}
                  required
                />

                <div className="modal-actions">
                  <button type="button" className="btn-delete" onClick={handleDeleteUser}>
                    Hapus
                  </button>
                  <button type="button" className="btn-cancel" onClick={() => setShowUpdateForm(false)}>
                    Batal
                  </button>
                  <button type="submit" className="btn-save">
                    Simpan
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPhoto && (
          <div className="modal-overlay" onClick={() => setShowPhoto(false)} style={{ zIndex: 2000 }}>
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              src={photoUrl}
              alt="Foto Nasabah"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                borderRadius: 16,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
